import {
  COMPANY_ADDRESS,
  COMPANY_LEGAL_NAME,
  PRIVACY_EMAIL,
  PRODUCT_NAME,
  SUPPORT_EMAIL
} from "../lib/brand";
import LegalPage, { Section } from "../components/LegalPage";

const EFFECTIVE = "October 17, 2026";

export default function Privacy() {
  return (
    <LegalPage title="Privacy Policy" effective={EFFECTIVE}>
      <Section heading="Who this covers">
        <p>
          {PRODUCT_NAME} is operated by {COMPANY_LEGAL_NAME}. This policy covers two different groups
          of people: our <strong>customers</strong>, who sign in and build assistants, and our
          customers' <strong>visitors</strong>, who chat with an assistant on someone else's website.
          Where the two differ, it says so.
        </p>
      </Section>

      <Section heading="1. Information we collect from customers">
        <ul className="list-inside list-disc space-y-1">
          <li>
            <strong>Account information</strong>: your email address, display name and profile photo,
            provided by Google Sign-In or by you at sign-up. Authentication is handled by Firebase
            Authentication; we never see or store your password.
          </li>
          <li>
            <strong>Content you upload</strong>: the documents, Q&amp;A pairs, text and URLs you give
            an assistant, plus the settings and appearance you choose.
          </li>
          <li>
            <strong>Billing information</strong>: your subscription status and invoice history.
            Card details go directly to Stripe and are never sent to or stored on our servers.
          </li>
          <li>
            <strong>Operational logs</strong>: errors and request records we need to keep the service
            running.
          </li>
        </ul>
      </Section>

      <Section heading="2. Information collected from visitors who chat">
        <p>
          When someone uses an assistant on a customer's site, we store the questions asked, the
          answers given, the page's origin, the browser type, a truncated IP address prefix, and
          counters used to enforce question limits. We do this so the customer can review
          conversations and so limits cannot be bypassed.
        </p>
        <p>
          On a free assistant, visitors also get a daily question limit. To count it without cookies,
          we combine the visitor's IP address and browser type into a one-way code that cannot be
          turned back into either. We keep that code and the day's count for two days.
        </p>
        <p>
          If a visitor asks for a person or books an appointment, the name, contact details and
          message they give are sent to the customer by email, Slack, Discord, webhook or the
          customer's booking tool, whichever the customer has connected, and a copy is kept so the customer can
          see it in the dashboard.
        </p>
        <p>
          Someone at the customer's business can take over a conversation and reply to the visitor
          in person. While they do, the visitor's messages go to that person rather than the
          assistant, and both sides are stored with the rest of the transcript, under the name the
          person uses in the dashboard. To show whether the visitor is still there, the chat window
          checks in every few seconds while it is open, and we note when it last did.
        </p>
        <p>
          If the customer connects their own API, the assistant may send it values a visitor gave in
          the chat, such as an order number, to look something up, and may repeat what the API
          answers. We send only the lookups the customer has turned on, and we keep a record of each
          call (what was asked, whether it worked, and when) so the customer can check it.
        </p>
        <p>
          The chat widget sets <strong>no cookies</strong>, and stores nothing in the visitor's browser
          until they open the chat. Once they do, it keeps one entry in local storage on that site: a
          code identifying the conversation, when it started, and whether the chat window was open, so
          a refresh or another page carries on the same conversation. It holds no personal
          information, is removed when the conversation expires (after 12 hours at most), and is not
          shared with other sites. The widget does not track visitors across sites, and it does not
          build a profile of anyone.
        </p>
        <p>
          Visitors may type personal information into a chat box. We ask customers not to solicit it,
          but where it happens the customer is the controller of that data and we process it on their
          behalf. Visitor requests about that data should go to the site that hosts the assistant.
        </p>
      </Section>

      <Section heading="3. How we use information">
        <ul className="list-inside list-disc space-y-1">
          <li>To create, host and run your assistants.</li>
          <li>To answer questions using the content you supplied.</li>
          <li>To bill you, and to send receipts and service notices.</li>
          <li>To provide support and investigate abuse.</li>
          <li>To keep the service secure and working.</li>
        </ul>
        <p>
          We do not sell personal information, and we do not use your content to train models
          offered to anyone else. The only thing we share for advertising is what section 8
          describes: measuring whether our own ads led to a sign-up.
        </p>
      </Section>

      <Section heading="4. Third-party processors">
        <ul className="list-inside list-disc space-y-1">
          <li>
            <strong>Google Cloud Platform and Firebase</strong>: hosting, authentication, databases,
            file storage, and the Vertex AI services (Gemini models and text embeddings) that read
            and index your content and generate answers.
          </li>
          <li>
            <strong>Stripe</strong>: payment processing, subscriptions and invoices.
          </li>
          <li>
            <strong>Brevo</strong>: sending email, including handoff emails that carry a visitor's
            details to the customer.
          </li>
          <li>
            <strong>Google Ads</strong>: measuring whether our own ads lead to sign-ups and
            subscriptions on our site (section 8).
          </li>
          <li>
            <strong>Google Analytics</strong>: how visitors use our own website and dashboard
            (section 8).
          </li>
        </ul>
        <p>
          Each processes data under its own terms and under agreements that limit what it may do with
          it.
        </p>
      </Section>

      <Section heading="5. Where data is processed">
        <p>
          Data is processed in the United States on Google Cloud infrastructure. If you are outside
          the US, using the service means your information is transferred there.
        </p>
      </Section>

      <Section heading="6. Data sharing">
        <p>
          We share information only with the processors above, when you ask us to, or when we are
          legally required to. If the business is ever sold or merged, customer data may transfer as
          part of it, and we will say so before it happens.
        </p>
      </Section>

      <Section heading="7. Security">
        <p>
          All traffic is encrypted in transit. The dashboard has no direct access to our databases.
          Every read and write goes through an authenticated API that checks your membership of the
          account first. Secrets and API keys are held server-side only. No system is perfectly
          secure, but this is the standard we hold ourselves to.
        </p>
      </Section>

      <Section heading="8. Cookies and tracking">
        <p>
          The dashboard uses Firebase Authentication, which stores a sign-in token in your browser so
          you stay signed in. It also keeps a few settings in your browser's storage: whether you
          picked light or dark mode, which account you last used, and an unfinished assistant from
          the start page until you sign in or close the tab.
        </p>
        <p>
          Our own website and the dashboard also load Google's advertising tag (Google Ads), which
          sets cookies to measure whether our ads led someone to build an assistant or buy a plan.
          When you create an assistant, your email address is scrambled (hashed) in your browser
          and sent to Google, so that sign-up can be matched to an ad you clicked; Google receives
          none of your content and none of your customers' conversations.
        </p>
        <p>
          The same pages use Google Analytics, which sets cookies to tell us how visitors use our
          site: which pages they open, how far they scroll, which buttons they click, and whether
          they go on to sign up, build an assistant or buy a plan. Addresses are stripped of
          anything private before they are sent, such as the one-time codes in our emails, and no
          event carries your name, your email address or anything you typed. We run no other
          advertising trackers or analytics.
        </p>
        <p>
          The embeddable widget, as above, sets no cookies and stores only the conversation a
          visitor has opened. Neither the advertising tag nor Google Analytics ever runs inside it,
          on your website or on your assistant's share page.
        </p>
      </Section>

      <Section heading="9. Data retention">
        <p>
          We keep your account and content while your account is open. Deleting your account removes
          account data and uploaded content within 30 days, except for records we are required to
          keep for tax and accounting.
        </p>
        <p>
          Conversation transcripts are deleted automatically about 30 days after the conversation,
          whether the assistant is running or archived. Handoff and booking records are kept while
          the assistant exists and are deleted with it. Knowledge a customer removes from an
          assistant, and any document uploaded with it, can be restored for 30 days and is then
          deleted. Records of calls to a customer's connected
          API, including the start of each answer, are deleted automatically after 30 days. Counters used for question limits and abuse
          prevention are deleted within a few days.
        </p>
      </Section>

      <Section heading="10. Your rights">
        <p>
          You may request access to, correction of, a copy of, or deletion of your personal
          information at any time by emailing {PRIVACY_EMAIL}. Depending on where you live you may
          have additional rights under the GDPR, the UK GDPR, or the CCPA/CPRA, including the right
          not to have personal information sold or shared, which we do not do in any case. We will not
          discriminate against you for exercising any of them.
        </p>
      </Section>

      <Section heading="11. Children's privacy">
        <p>
          {PRODUCT_NAME} is a business product and is not directed at children under 13. We do not
          knowingly collect their information; if we learn we have, we delete it.
        </p>
      </Section>

      <Section heading="12. Changes to this policy">
        <p>
          We will post any changes here and update the effective date. Material changes will be
          announced by email or in the dashboard before they take effect.
        </p>
      </Section>

      <Section heading="13. Contact">
        <p>
          {COMPANY_LEGAL_NAME}
          <br />
          {COMPANY_ADDRESS}
          <br />
          Privacy:{" "}
          <a className="text-brand-700 underline" href={`mailto:${PRIVACY_EMAIL}`}>
            {PRIVACY_EMAIL}
          </a>
          <br />
          Support:{" "}
          <a className="text-brand-700 underline" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
        </p>
      </Section>
    </LegalPage>
  );
}
